import { AvatarGrid } from "@/components/avatar-grid";
import Image from "next/image";
import { LIBUM_AVATARS, LIBUM_ORG_AVATARS } from "@/lib/constants";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

interface AvatarPopoverProps {
  avatar: string;
  type: "user" | "organization";
  onAvatarChange: (src: string) => void;
}

export default function AvatarPopover({
  avatar,
  type,
  onAvatarChange,
}: AvatarPopoverProps) {
  const avatars = type === "user" ? LIBUM_AVATARS : LIBUM_ORG_AVATARS;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Choose ${type} avatar`}
          className={`mr-2 flex size-12 min-h-12 min-w-12 shrink-0 items-center justify-items-center overflow-hidden rounded-full border-4 border-amber-400 bg-background p-0 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary active:scale-95 ${isOpen && "scale-110"}`}
        >
          <Image
            src={avatar}
            alt="Avatar"
            width={50}
            height={50}
            className="size-full object-cover"
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        sideOffset={8}
        collisionPadding={12}
        className="max-h-[var(--radix-dropdown-menu-content-available-height)] w-96 max-w-[calc(100vw-24px)] overflow-y-auto"
      >
        <TooltipProvider delayDuration={200} disableHoverableContent={true}>
          <div className="p-2">
            <AvatarGrid size="sm">
              {avatars.map((option) => (
                <Tooltip key={option.name}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-pressed={avatar === option.src}
                      className={`flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-background p-0 transition-transform hover:scale-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary active:scale-95 ${avatar === option.src ? "border-4 border-amber-400" : "border-2 border-input dark:border-backgroundAccent"}`}
                      onClick={() => {
                        onAvatarChange(option.src);
                        setIsOpen(false);
                      }}
                    >
                      <Image
                        src={option.src}
                        alt={option.name}
                        width={50}
                        height={50}
                        className="size-full object-cover"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{option.name}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </AvatarGrid>
          </div>
        </TooltipProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
